import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getDashboardMetrics, getProjects, getShifts } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LoadingSpinner, StatusBadge } from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { GuideButton } from "../components/common/GuideButton";
// EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
import { formatProjectClient } from "../lib/projectClient";
import {
  Clock,
  Users,
  UserCheck,
  FolderKanban,
  Building2,
  Calendar,
  ChevronRight,
  ExternalLink,
  Activity,
  Database,
  HardDrive,
  Cpu,
  Globe,
  Mail,
  Eye,
  FileText,
  Server,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  TableProperties,
  Plane,
  UserX,
} from "lucide-react";
import { APP_VERSION, BUILD_TIMESTAMP, PLATFORM_BUILD, PWA_BUILD } from "../lib/version";
import { Button } from "../components/ui/button";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [weekProjects, setWeekProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1
  // Default to 'active' — operationally useful work only. Persisted to
  // localStorage so an admin's choice survives reload. Reset to default
  // is one click on the Active chip.
  const WEEK_FILTER_KEY = "ef:dashboard:weekProjectsFilter:v1";
  const VALID_FILTERS = ["active", "drafts", "no_client", "oot", "all"];
  const [weekFilter, setWeekFilter] = useState(() => {
    if (typeof window === "undefined") return "active";
    const saved = window.localStorage.getItem(WEEK_FILTER_KEY);
    return VALID_FILTERS.includes(saved) ? saved : "active";
  });
  const setAndPersistWeekFilter = (v) => {
    setWeekFilter(v);
    try {
      window.localStorage.setItem(WEEK_FILTER_KEY, v);
    } catch {
      /* localStorage unavailable */
    }
  };
  const { formatCurrency, formatDate } = useFormatters();
  
  // EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Platform health state
  const [platformHealth, setPlatformHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    loadData();
  }, []);
  
  // EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Load platform health for superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      loadPlatformHealth();
    }
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [metricsRes, projectsRes, shiftsRes] = await Promise.all([
        getDashboardMetrics(),
        getProjects(),
        getShifts(),
      ]);
      setMetrics(metricsRes.data);
      
      // Get this week's date range
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
      endOfWeek.setHours(23, 59, 59, 999);
      
      // Filter projects that have shifts this week
      const shiftsThisWeek = (shiftsRes.data || []).filter(shift => {
        const shiftDate = new Date(shift.date);
        return shiftDate >= startOfWeek && shiftDate <= endOfWeek;
      });
      
      // Get unique project IDs from this week's shifts
      const projectIdsThisWeek = [...new Set(shiftsThisWeek.map(s => s.project_id))];
      
      // Get project details with shift counts.
      // EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1: enrich each row with
      // the signals we need to filter and badge (status, client_id, OOT).
      const projectsWithShifts = (projectsRes.data || [])
        .filter(p => projectIdsThisWeek.includes(p.id))
        .map(project => {
          const projectShifts = shiftsThisWeek.filter(s => s.project_id === project.id);
          const hasOOT = projectShifts.some(s => s.out_of_town === true);
          // EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
          // Resolve the client display once per row using the canonical
          // helper. The backend now enriches `client_name`; for backwards
          // compatibility we still honour the helper's fallback chain.
          const clientDisplay = formatProjectClient(project);
          return {
            ...project,
            shiftsThisWeek: projectShifts.length,
            nextShiftDate: projectShifts.sort((a, b) => new Date(a.date) - new Date(b.date))[0]?.date,
            hasOOT,
            // True "no client" — missing client_id. Misnamed/missing-name
            // rows are NOT counted here so the chip stays trustworthy.
            isNoClient: clientDisplay.isMissingClientId,
            isDanglingClientLink: clientDisplay.isDanglingClientLink,
            clientDisplayLabel: clientDisplay.label,
            isDraft: project.status === "draft",
            isCancelled: project.status === "cancelled",
            isCompleted: project.status === "completed",
          };
        })
        .sort((a, b) => new Date(a.nextShiftDate) - new Date(b.nextShiftDate));
      
      setWeekProjects(projectsWithShifts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Load platform health data
  const loadPlatformHealth = async () => {
    try {
      setHealthLoading(true);
      const response = await api.get('/admin/platform-health');
      setPlatformHealth(response.data);
    } catch (err) {
      console.error('Failed to load platform health:', err);
    } finally {
      setHealthLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-400">
        Error loading dashboard: {error}
      </div>
    );
  }

  // Quick stat component for compactness
  const QuickStat = ({ label, value, icon: Icon, onClick }) => (
    <div 
      className={`flex items-center gap-3 p-3 bg-surface-elevated/20 rounded-sm hover:bg-surface-elevated/30 transition-colors ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="p-2 bg-primary/10 rounded">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-lg font-semibold font-mono">{value}</p>
      </div>
      {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
    </div>
  );

  // EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Service status indicator
  const ServiceStatus = ({ name, status, message, icon: Icon }) => {
    const statusConfig = {
      connected: { color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle2 },
      error: { color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle },
      unavailable: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.unavailable;
    const StatusIcon = config.icon;
    
    return (
      <div className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded-sm">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded ${config.bg}`}>
            <Icon className={`h-4 w-4 ${config.color}`} />
          </div>
          <div>
            <p className="text-sm font-medium">{name}</p>
            <p className="text-xs text-muted-foreground">{message}</p>
          </div>
        </div>
        <StatusIcon className={`h-5 w-5 ${config.color}`} />
      </div>
    );
  };

  // EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Resource usage bar
  const ResourceBar = ({ label, percent, used, total, unit = 'GB' }) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">{used} / {total} {unit}</span>
      </div>
      <div className="h-2 bg-surface-elevated/50 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            percent > 90 ? 'bg-red-500' : percent > 70 ? 'bg-yellow-500' : 'bg-green-500'
          }`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground text-right">{percent}% used</p>
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header with Version */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-secondary tracking-tight uppercase text-foreground">
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Quick overview of your workforce operations
          </p>
        </div>
        {/* EVENTSFLO-USER-GUIDE-V1: in-app Guide trigger + build info */}
        <div className="flex items-start gap-3">
          <GuideButton testId="dashboard-guide-btn" />
          {/* Build Info - Exactly 2 lines, label and value on same line */}
          <div className="text-right font-mono text-xs whitespace-nowrap">
            <div className="text-muted-foreground">
              Platform Build: <span className="text-primary font-semibold">{PLATFORM_BUILD}</span>
            </div>
            <div className="text-muted-foreground">
              PWA Build: <span className="text-muted-foreground">{PWA_BUILD}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics - Compact Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickStat 
          label="Active Shifts" 
          value={metrics?.active_shifts || 0} 
          icon={Clock}
          onClick={() => navigate("/shifts")}
        />
        <QuickStat 
          label="On Site Today" 
          value={metrics?.staff_on_site_today || 0} 
          icon={UserCheck}
          onClick={() => navigate("/assignments")}
        />
        <QuickStat 
          label="Total Crew" 
          value={metrics?.total_staff || 0} 
          icon={Users}
          onClick={() => navigate("/crew-management")}
        />
        <QuickStat 
          label="Active Projects" 
          value={metrics?.active_projects || 0} 
          icon={FolderKanban}
          onClick={() => navigate("/projects")}
        />
      </div>

      {/* EF-SUPERADMIN-PLATFORM-HEALTH-DASHBOARD-V1: Platform Health Section (SuperAdmin Only) */}
      {isSuperAdmin && (
        <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/20" data-testid="platform-health-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-blue-400 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Platform Health
                <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/30">
                  SuperAdmin
                </Badge>
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadPlatformHealth}
                disabled={healthLoading}
                className="text-blue-400 hover:bg-blue-500/10"
              >
                <RefreshCw className={`h-4 w-4 ${healthLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {healthLoading && !platformHealth ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-400" />
              </div>
            ) : platformHealth ? (
              <div className="space-y-6">
                {/* Platform Metrics */}
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Platform Metrics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <Building2 className="h-4 w-4 mx-auto mb-1 text-blue-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.tenant_count}</p>
                      <p className="text-[10px] text-muted-foreground">Tenants</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <Users className="h-4 w-4 mx-auto mb-1 text-green-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.user_count}</p>
                      <p className="text-[10px] text-muted-foreground">Users</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <Activity className="h-4 w-4 mx-auto mb-1 text-purple-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.active_sessions}</p>
                      <p className="text-[10px] text-muted-foreground">Sessions (24h)</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <UserCheck className="h-4 w-4 mx-auto mb-1 text-cyan-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.staff_count}</p>
                      <p className="text-[10px] text-muted-foreground">Crew Records</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <FolderKanban className="h-4 w-4 mx-auto mb-1 text-yellow-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.project_count}</p>
                      <p className="text-[10px] text-muted-foreground">Projects</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <Clock className="h-4 w-4 mx-auto mb-1 text-orange-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.shift_count}</p>
                      <p className="text-[10px] text-muted-foreground">Shifts</p>
                    </div>
                    <div className="text-center p-3 bg-surface-elevated/20 rounded-sm">
                      <FileText className="h-4 w-4 mx-auto mb-1 text-pink-400" />
                      <p className="text-lg font-bold font-mono">{platformHealth.platform_metrics.document_count}</p>
                      <p className="text-[10px] text-muted-foreground">Documents</p>
                    </div>
                  </div>
                </div>

                {/* Service Status & Resources */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Service Status */}
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Service Status</h4>
                    <div className="space-y-2">
                      <ServiceStatus 
                        name="Database" 
                        status={platformHealth.service_status.database?.status}
                        message={platformHealth.service_status.database?.message}
                        icon={Database}
                      />
                      <ServiceStatus 
                        name="Backend API" 
                        status={platformHealth.service_status.backend_api?.status}
                        message={platformHealth.service_status.backend_api?.message}
                        icon={Server}
                      />
                      <ServiceStatus 
                        name="Google Vision OCR" 
                        status={platformHealth.service_status.google_vision_ocr?.status}
                        message={platformHealth.service_status.google_vision_ocr?.message}
                        icon={Eye}
                      />
                      <ServiceStatus 
                        name="Google Routes API" 
                        status={platformHealth.service_status.google_routes_api?.status}
                        message={platformHealth.service_status.google_routes_api?.message}
                        icon={Globe}
                      />
                      <ServiceStatus 
                        name="IMAP Email" 
                        status={platformHealth.service_status.imap_email?.status}
                        message={platformHealth.service_status.imap_email?.message}
                        icon={Mail}
                      />
                    </div>
                  </div>

                  {/* Resource Monitoring */}
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Resource Monitoring</h4>
                    <div className="space-y-4 p-4 bg-surface-elevated/20 rounded-sm">
                      {platformHealth.resource_monitoring.disk_usage?.percent !== undefined ? (
                        <ResourceBar 
                          label="Disk Usage"
                          percent={platformHealth.resource_monitoring.disk_usage.percent}
                          used={platformHealth.resource_monitoring.disk_usage.used_gb}
                          total={platformHealth.resource_monitoring.disk_usage.total_gb}
                        />
                      ) : (
                        <div className="text-xs text-muted-foreground">Disk info unavailable</div>
                      )}
                      
                      {platformHealth.resource_monitoring.memory_usage?.percent !== undefined ? (
                        <ResourceBar 
                          label="Memory Usage"
                          percent={platformHealth.resource_monitoring.memory_usage.percent}
                          used={platformHealth.resource_monitoring.memory_usage.used_gb}
                          total={platformHealth.resource_monitoring.memory_usage.total_gb}
                        />
                      ) : (
                        <div className="text-xs text-muted-foreground">Memory info unavailable</div>
                      )}
                      
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                          <Cpu className="h-3 w-3" />
                          API Response Time
                        </span>
                        <span className={`font-mono text-sm ${
                          platformHealth.resource_monitoring.api_response_time_ms < 100 
                            ? 'text-green-400' 
                            : platformHealth.resource_monitoring.api_response_time_ms < 500 
                              ? 'text-yellow-400' 
                              : 'text-red-400'
                        }`}>
                          {platformHealth.resource_monitoring.api_response_time_ms}ms
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Last Updated */}
                <div className="text-xs text-muted-foreground text-right">
                  Last updated: {new Date(platformHealth.timestamp).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Unable to load platform health data</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1: Crew Planner primary CTA */}
      <Card
        className="bg-card border-white/5 hover:border-primary/40 transition-colors cursor-pointer"
        data-testid="crew-planner-cta-card"
        onClick={() => navigate("/planner/sheet")}
      >
        <CardContent className="p-4 flex items-center gap-4">
          <div className="p-3 bg-primary/10 rounded-md shrink-0">
            <TableProperties className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm uppercase tracking-wide">
              Open Crew Planner
            </p>
            <p className="text-xs text-muted-foreground">
              Plan and assign crew across projects and shifts.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm text-xs uppercase tracking-wide"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/planner/sheet");
            }}
            data-testid="crew-planner-cta-btn"
          >
            Open
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {/* This Week's Scheduled Projects */}
      <Card className="bg-card border-white/5" data-testid="week-projects-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              This Week's Projects
            </CardTitle>
            <Link 
              to="/projects" 
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              View All <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          {/* EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1: filter chips +
              transparent counts. Defaults to "Active" (cleanest list). */}
          {(() => {
            const total = weekProjects.length;
            const draftsCount = weekProjects.filter((p) => p.isDraft).length;
            const cancelledCount = weekProjects.filter((p) => p.isCancelled).length;
            const completedCount = weekProjects.filter((p) => p.isCompleted).length;
            const noClientCount = weekProjects.filter((p) => p.isNoClient).length;
            const ootCount = weekProjects.filter((p) => p.hasOOT).length;
            const activeCount = weekProjects.filter(
              (p) => !p.isDraft && !p.isCancelled && !p.isNoClient && !p.isCompleted,
            ).length;
            const chips = [
              { key: "active", label: "Active", count: activeCount, title: "Confirmed, client-linked projects only" },
              { key: "drafts", label: "Drafts", count: draftsCount, title: "Draft projects this week" },
              { key: "no_client", label: "No client", count: noClientCount, title: "Projects with no client linked" },
              { key: "oot", label: "OOT", count: ootCount, title: "Projects with an out-of-town shift this week" },
              { key: "all", label: "All", count: total, title: "Every project with a shift this week" },
            ];
            return (
              <div
                className="mt-3 flex flex-wrap items-center gap-1.5"
                role="tablist"
                data-testid="week-projects-filter-chips"
              >
                {chips.map((c) => {
                  const isActive = weekFilter === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setAndPersistWeekFilter(c.key)}
                      title={c.title}
                      data-testid={`week-projects-filter-${c.key}`}
                      className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                        isActive
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "border-transparent text-muted-foreground hover:text-foreground hover:border-white/10"
                      }`}
                    >
                      {c.label}
                      <span className="ml-1.5 font-mono opacity-70">{c.count}</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </CardHeader>
        <CardContent>
          {(() => {
            // EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1: apply filter.
            let visible = weekProjects;
            if (weekFilter === "active") {
              visible = weekProjects.filter(
                (p) => !p.isDraft && !p.isCancelled && !p.isNoClient && !p.isCompleted,
              );
            } else if (weekFilter === "drafts") {
              visible = weekProjects.filter((p) => p.isDraft);
            } else if (weekFilter === "no_client") {
              visible = weekProjects.filter((p) => p.isNoClient);
            } else if (weekFilter === "oot") {
              visible = weekProjects.filter((p) => p.hasOOT);
            }

            if (visible.length === 0) {
              return (
                <div
                  className="text-center py-6 text-muted-foreground"
                  data-testid="week-projects-empty"
                >
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {weekFilter === "active"
                      ? "No active client-linked projects this week."
                      : "No projects this week for the selected filter."}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    <Link
                      to="/projects"
                      className="text-xs px-2.5 py-1 rounded-sm border border-white/10 hover:border-primary/40 hover:text-primary transition-colors"
                      data-testid="week-projects-empty-view-all"
                    >
                      View all projects
                    </Link>
                    <Link
                      to="/planner/sheet"
                      className="text-xs px-2.5 py-1 rounded-sm border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                      data-testid="week-projects-empty-open-planner"
                    >
                      Open Crew Planner
                    </Link>
                    {weekFilter === "active" && weekProjects.some((p) => p.isDraft) && (
                      <button
                        type="button"
                        onClick={() => setAndPersistWeekFilter("drafts")}
                        className="text-xs px-2.5 py-1 rounded-sm border border-white/10 hover:border-amber-400/60 hover:text-amber-300 transition-colors"
                        data-testid="week-projects-empty-show-drafts"
                      >
                        Show drafts
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-2">
                {visible.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded-sm hover:bg-surface-elevated/30 transition-colors group"
                    data-testid={`week-project-${project.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-primary/10 rounded">
                        <FolderKanban className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                          {project.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {project.clientDisplayLabel} • Next: {formatDate(project.nextShiftDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* EVENTSFLO-DASHBOARD-LIST-FILTER-CLEANUP-V1
                          Reason-this-row-is-here badges. Only render in
                          non-active views to keep the default list crisp. */}
                      {weekFilter !== "active" && project.isDraft && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-400/30"
                          data-testid={`week-project-badge-draft-${project.id}`}
                        >
                          Draft
                        </Badge>
                      )}
                      {weekFilter !== "active" && project.isCancelled && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-zinc-500/10 text-zinc-300 border-zinc-400/30"
                          data-testid={`week-project-badge-cancelled-${project.id}`}
                        >
                          Cancelled
                        </Badge>
                      )}
                      {weekFilter !== "active" && project.isCompleted && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-400/30"
                          data-testid={`week-project-badge-completed-${project.id}`}
                        >
                          Completed
                        </Badge>
                      )}
                      {weekFilter !== "active" && project.isNoClient && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-rose-500/10 text-rose-300 border-rose-400/30"
                          data-testid={`week-project-badge-noclient-${project.id}`}
                        >
                          <UserX className="h-2.5 w-2.5 mr-0.5" />
                          No client
                        </Badge>
                      )}
                      {/* EVENTSFLO-PROJECT-CLIENT-NAME-DISPLAY-FALLBACK-AUDIT-V1
                          Admin-only warning when a project has a client_id
                          that no longer resolves (dangling link). Visible
                          in every view because it's a data-quality signal. */}
                      {project.isDanglingClientLink && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-400/40"
                          data-testid={`week-project-badge-client-link-missing-${project.id}`}
                          title={`Client id ${project.client_id} does not resolve in this tenant`}
                        >
                          <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                          Client link missing
                        </Badge>
                      )}
                      {project.hasOOT && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-sky-500/10 text-sky-300 border-sky-400/30"
                          data-testid={`week-project-badge-oot-${project.id}`}
                        >
                          <Plane className="h-2.5 w-2.5 mr-0.5" />
                          OOT
                        </Badge>
                      )}
                      <div className="text-right">
                        <p className="text-sm font-mono font-semibold">{project.shiftsThisWeek}</p>
                        <p className="text-xs text-muted-foreground">shifts</p>
                      </div>
                      <StatusBadge status={project.status} />
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickStat 
          label="Total Clients" 
          value={metrics?.total_clients || 0} 
          icon={Building2}
          onClick={() => navigate("/clients")}
        />
        <QuickStat 
          label="Pending Quotes" 
          value={metrics?.pending_quotes || 0} 
          icon={Clock}
          onClick={() => navigate("/quotes")}
        />
        <QuickStat 
          label="Unpaid Invoices" 
          value={metrics?.unpaid_invoices || 0} 
          icon={Clock}
          onClick={() => navigate("/invoices")}
        />
        <QuickStat 
          label="Outstanding" 
          value={formatCurrency(metrics?.total_outstanding || 0)} 
          icon={Clock}
        />
      </div>

      {/* Footer with System Status & Build Info */}
      <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-white/5 pt-4">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            System Operational
          </span>
          <span>•</span>
          <span>DB Connected</span>
          <span>•</span>
          <span>API Healthy</span>
        </div>
        <div className="flex flex-col items-end gap-0.5 font-mono text-[10px]">
          <span>Platform Build: {PLATFORM_BUILD}</span>
          <span>PWA Build: {PWA_BUILD}</span>
        </div>
      </div>
    </div>
  );
}
